import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { 
  DollarSign, 
  CheckCircle2,
  AlertTriangle,
  X,
  Loader2,
  AlertCircle,
  CreditCard,
  Wallet
} from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

interface GiftGroupContributionProps {
  onNavigate: (screen: string, data?: { giftGroupId?: string; tab?: string; recommendedAmount?: number }) => void;
  giftGroupId: string | null;
  memberId?: string | null;
}

interface GiftGroupDetails {
  id: string;
  title: string;
  type_id: number;
  type_name: string;
  gift_date: string;
  recommended_contribution: number | null;
  total_members: number;
  accepted_members: number;
}

export function GiftGroupContribution({ onNavigate, giftGroupId, memberId }: GiftGroupContributionProps) {
  const [giftGroup, setGiftGroup] = useState<GiftGroupDetails | null>(null);
  const [userBalance, setUserBalance] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRemoved, setIsRemoved] = useState(false);
  const [actualGiftGroupId, setActualGiftGroupId] = useState<string | null>(giftGroupId);

  useEffect(() => {
    // If neither giftGroupId nor memberId provided, show error
    if (!giftGroupId && !memberId) {
      setError('No gift group selected');
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        let groupData;
        
        // If we have memberId, fetch from invitation endpoint
        if (memberId) {
          groupData = await api.getGiftGroupInvitation(memberId);
          setActualGiftGroupId(groupData.gift_group_id);
        } else if (giftGroupId) {
          // If we have giftGroupId, fetch from contribution details endpoint
          groupData = await api.getGiftGroupContributionDetails(giftGroupId);
          setActualGiftGroupId(giftGroupId);
        } else {
          throw new Error('No gift group or member ID provided');
        }

        if (groupData) {
          setGiftGroup({
            id: groupData.gift_group_id,
            title: groupData.gift_group_title,
            type_id: groupData.gift_group_type_id,
            type_name: groupData.gift_group_type_name,
            gift_date: groupData.gift_date,
            recommended_contribution: groupData.recommended_contribution,
            total_members: groupData.total_members,
            accepted_members: groupData.accepted_members,
          });
        }

        // Fetch user's credit balance
        const profile = await api.getProfile();
        setUserBalance(profile.creditBalance);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load gift group details';
        console.error('Gift group contribution details error:', {
          error: err,
          giftGroupId,
          memberId,
          errorMessage
        });
        setError(errorMessage);
        toast.error('Failed to load details', {
          description: errorMessage,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [giftGroupId, memberId]);

  const handleContributeFromBalance = async () => {
    if (!giftGroup || !giftGroup.recommended_contribution || !actualGiftGroupId) return;

    const amount = giftGroup.recommended_contribution;

    if (userBalance < amount) {
      toast.error('Insufficient balance', {
        description: `You need $${amount.toFixed(2)} but only have $${userBalance.toFixed(2)}`,
      });
      return;
    }

    try {
      setIsProcessing(true);
      await api.contributeToGiftGroup(actualGiftGroupId, amount);

      toast.success('Contribution recorded!', {
        description: `You contributed $${amount.toFixed(2)} to ${giftGroup.title}.`,
      });
      
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        onNavigate('dashboard', { tab: 'todo' });
      }, 2000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to contribute';
      toast.error('Contribution failed', {
        description: errorMessage,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemove = () => {
    setIsRemoved(true);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20 flex items-center justify-center p-4">
        <Card className="p-8 border border-primary/20 shadow-lg">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading gift group details...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (error || !giftGroup) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20 flex items-center justify-center p-4">
        <Card className="p-8 border border-primary/20 shadow-lg max-w-md">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error || 'Gift group not found'}</AlertDescription>
          </Alert>
          <Button
            className="mt-4 w-full bg-primary hover:bg-primary/90"
            onClick={() => onNavigate('dashboard', { tab: 'todo' })}
          >
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  if (isRemoved) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 border border-primary/20 shadow-lg text-center">
          <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
            <X className="w-8 h-8 text-muted-foreground" />
          </div>
          <h2 className="text-primary mb-2">Removed from Gift Group</h2>
          <p className="text-muted-foreground mb-6">You've been removed from {giftGroup.title}</p>
          <Button onClick={() => onNavigate('dashboard')} className="w-full bg-primary hover:bg-primary/90">
            Return to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  const recommendedAmount = giftGroup.recommended_contribution || 0;
  const hasEnoughBalance = userBalance >= recommendedAmount;
  const neededAmount = recommendedAmount - userBalance;

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl p-8 border border-primary/20 shadow-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-primary mb-2">{giftGroup.title}</h1>
          <h2 className="text-muted-foreground">Gift Group Contribution</h2>
        </div>

        {/* Group Type Info */}
        <Card className="p-4 bg-primary/5 border border-primary/20 mb-6">
          <div className="text-sm">
            <strong>{giftGroup.type_name}:</strong> This gift group needs your contribution to make the gift happen!
          </div>
        </Card>

        {/* Info Note */}
        <Alert className="mb-6 border-primary/20">
          <DollarSign className="h-4 w-4" />
          <AlertDescription className="text-sm">
            <strong>After contributing:</strong> You'll be able to suggest gifts, vote on rankings, 
            and track the gift order status. The gift will be automatically ordered on time!
          </AlertDescription>
        </Alert>

        {/* Contribution Details */}
        <Card className="p-6 border border-primary/20 mb-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-4 border-b border-primary/20">
              <div>
                <div className="text-sm text-muted-foreground">Recommended Contribution</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Gift Date: {formatDate(giftGroup.gift_date)}
                </div>
              </div>
              <div className="text-2xl text-primary">{formatCurrency(recommendedAmount)}</div>
            </div>

            <div className="bg-secondary/20 p-4 rounded-lg space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="flex items-center gap-2">
                  <Wallet className="w-4 h-4" />
                  Your Credit Balance
                </span>
                <span className={userBalance >= recommendedAmount ? 'text-green-600' : 'text-orange-600'}>
                  {formatCurrency(userBalance)}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t border-primary/20">
                <strong>Required Contribution</strong>
                <strong>{formatCurrency(recommendedAmount)}</strong>
              </div>
            </div>
          </div>
        </Card>

        {/* Payment Options */}
        <div className="mb-6 space-y-4">
          <div className="text-sm text-muted-foreground text-center">Choose how you'd like to contribute:</div>

          {/* Option 1: Pay from Credit Balance */}
          {hasEnoughBalance ? (
            <Card className="p-4 border-2 border-primary/20 bg-primary/5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Wallet className="w-5 h-5 text-primary" />
                  <div>
                    <div>Pay from Credit Balance</div>
                    <div className="text-xs text-muted-foreground">
                      Balance after: {formatCurrency(userBalance - recommendedAmount)}
                    </div>
                  </div>
                </div>
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <Button
                onClick={handleContributeFromBalance}
                disabled={isProcessing}
                size="lg"
                className="w-full bg-primary hover:bg-primary/90"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Contribute {formatCurrency(recommendedAmount)}
                  </>
                )}
              </Button>
            </Card>
          ) : (
            <Card className="p-4 border-2 border-orange-200 bg-orange-50">
              <div className="flex items-start gap-3 mb-3">
                <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <strong>Insufficient Balance:</strong> You need {formatCurrency(neededAmount)} more to contribute.
                </div>
              </div>
              <Button
                onClick={() => {
                  onNavigate('dashboard', { 
                    tab: 'finances', 
                    giftGroupId: actualGiftGroupId || giftGroup.id,
                    recommendedAmount: recommendedAmount
                  });
                }}
                disabled={isProcessing}
                size="lg"
                variant="outline"
                className="w-full border-orange-300 hover:bg-orange-100"
              >
                <CreditCard className="w-5 h-5 mr-2" />
                Add ${recommendedAmount.toFixed(2)} & Contribute
              </Button>
            </Card>
          )}

          {/* Option 2: Add Funds via PayPal (Even if they have enough) */}
          {hasEnoughBalance && (
            <details className="text-center">
              <summary className="text-sm text-muted-foreground cursor-pointer hover:text-foreground">
                Or add more funds to your balance first
              </summary>
              <div className="mt-4">
                <Button
                  onClick={() => {
                    onNavigate('dashboard', { tab: 'finances' });
                  }}
                  disabled={isProcessing}
                  size="lg"
                  variant="outline"
                  className="w-full"
                >
                  <CreditCard className="w-5 h-5 mr-2" />
                  Add Funds via PayPal
                </Button>
              </div>
            </details>
          )}
        </div>

        {/* Remove Option */}
        <div className="mt-6 pt-6 border-t border-primary/20">
          <Button
            variant="ghost"
            onClick={handleRemove}
            className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
            disabled={isProcessing}
          >
            Remove Me from This Gift Group
          </Button>
        </div>
      </Card>
    </div>
  );
}